import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ImagePlus, Film, Music, Type, Sparkles } from 'lucide-react';
import { layerWindowToVideoSegments } from '@/lib/layers/layerWindowToVideoSegments';
import { resolveLayerSeekTarget } from '@/lib/layers/resolveLayerSeekTarget';
import { applyLaneMove } from '@/lib/layers/applyLaneMove';
import { applyLaneTrim } from '@/lib/layers/applyLaneTrim';
import { laneRowForIndex, resolveLaneReorder } from '@/lib/layers/resolveLaneReorder';
import type { MediaLayer } from '@/lib/layers/types';
import { createRafScheduler } from '@/lib/dom/createRafScheduler';

interface Interval { startTime: number; endTime: number }

interface Props {
  layers: MediaLayer[];
  activeLayerId: string | null;
  intervals: Interval[];
  pxPerSecond: number;
  sequenceMode?: boolean;
  /** Vertical px offset of the first lane inside the timeline container. */
  laneTop: number;
  clipDuration: number;
  onSelect: (id: string | null) => void;
  onPatch: (id: string, patch: Partial<MediaLayer>) => void;
  /** Restack: move the layer to `toIndex` in the layers array. Omit to disable
   *  vertical lane dragging. */
  onReorder?: (id: string, toIndex: number) => void;
  /** Playhead in source-video time, used to decide whether selecting a lane
   *  should seek to the layer. */
  currentTime?: number;
  /** Move the playhead (source-video time) so the selected layer is on screen. */
  onSeek?: (videoTime: number) => void;
}

const TYPE_ICON = { image: ImagePlus, video: Film, audio: Music, text: Type, motion: Sparkles } as const;
const LANE_HEIGHT = 20;

/** A click is not a drag. Below this many pixels of pointer travel we don't
 *  touch the layer at all. Without it, a few pixels of twitch while clicking
 *  moves the layer by `dx / pxPerSecond` seconds — and pxPerSecond bottoms out
 *  at 1 (zoom 0.1), so a 3px twitch would shift the layer a full 3 seconds. */
const DRAG_THRESHOLD_PX = 4;

type DragState = {
  id: string;
  mode: 'move' | 'trim-start' | 'trim-end';
  /** Flips once the pointer travels past DRAG_THRESHOLD_PX horizontally; until
   *  then the gesture is still a click and must not patch the layer's time.
   *  Tracked separately from the vertical axis so that a pure lane-to-lane drag
   *  (dx ≈ 0) never nudges the layer along the timeline, and a pure time drag
   *  (dy ≈ 0) never restacks it. */
  movedX: boolean;
  /** Flips once the layer has actually changed lanes. */
  movedY: boolean;
  startClientX: number;
  startClientY: number;
  /** Array index at drag start. Target lanes are resolved against this, not
   *  against the live index, so the reorder is idempotent as the layer moves
   *  under the pointer mid-drag. */
  startIndex: number;
  original: MediaLayer;
};

/** Layer lane bars drawn in SOURCE-VIDEO coordinates (the ruler's axis).
 *  A window spanning a scene cut renders as multiple segments. All drags
 *  convert px → seconds on the video axis, which equals clip-time deltas
 *  inside kept intervals, and mutate the clip-time window via the pure
 *  applyLaneMove/applyLaneTrim helpers. */
export const LayerLanes: React.FC<Props> = ({
  layers, activeLayerId, intervals, pxPerSecond, laneTop, clipDuration, onSelect, onPatch,
  onReorder, currentTime = 0, onSeek, sequenceMode = false,
}) => {
  const { t } = useTranslation();
  const drag = useRef<DragState | null>(null);
  /** Whether the gesture that just ended moved the pointer past the threshold. */
  const gestureMovedRef = useRef(false);

  // Keep latest props in refs so the drag handlers below can have stable
  // identities (empty deps) without going stale. This matters because
  // Timeline passes a fresh `onPatch` closure every render — if the
  // handlers depended on it directly, every pointermove-triggered re-render
  // during a drag would produce a new `endDrag` identity, and the unmount
  // cleanup effect would then remove listeners with the OLD identity
  // (removeEventListener no-ops on identity mismatch), leaking listeners
  // and killing the drag after the first pointermove.
  const onSeekRef = useRef(onSeek);
  const currentTimeRef = useRef(currentTime);
  const intervalsRef = useRef(intervals);
  const onPatchRef = useRef(onPatch);
  const onReorderRef = useRef(onReorder);
  const layerCountRef = useRef(layers.length);
  const pxPerSecondRef = useRef(pxPerSecond);
  const clipDurationRef = useRef(clipDuration);
  onSeekRef.current = onSeek;
  currentTimeRef.current = currentTime;
  intervalsRef.current = intervals;
  onPatchRef.current = onPatch;
  onReorderRef.current = onReorder;
  const patchSchedulerRef = useRef(createRafScheduler(
    (next: { id: string; patch: Partial<MediaLayer> }) => {
      onPatchRef.current(next.id, next.patch);
    },
  ));
  layerCountRef.current = layers.length;
  pxPerSecondRef.current = pxPerSecond;
  clipDurationRef.current = clipDuration;

  const onPointerMove = useCallback((ev: PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const dxPx = ev.clientX - d.startClientX;
    const dyPx = ev.clientY - d.startClientY;

    // Vertical: restack. Only the body of a bar reorders — dragging a trim
    // handle is a horizontal gesture, and yanking the layer to another lane
    // mid-trim would be baffling.
    if (d.mode === 'move' && onReorderRef.current) {
      const toIndex = resolveLaneReorder({
        startIndex: d.startIndex,
        count: layerCountRef.current,
        dyPx,
        laneHeight: LANE_HEIGHT,
      });
      if (toIndex !== d.startIndex) d.movedY = true;
      // Cheap to call on every move: moveLayer returns the same array when the
      // layer is already at toIndex, so React bails out of the re-render.
      onReorderRef.current(d.id, toIndex);
    }

    // Horizontal: move/trim in time.
    if (!d.movedX) {
      if (Math.abs(dxPx) < DRAG_THRESHOLD_PX) return;
      d.movedX = true;
    }
    const deltaSec = dxPx / pxPerSecondRef.current;
    const l = d.original;
    if (d.mode === 'move') {
      patchSchedulerRef.current.schedule({
        id: d.id,
        patch: applyLaneMove({
          start: l.start, end: l.end, deltaSec, clipDuration: clipDurationRef.current,
        }),
      });
    } else {
      patchSchedulerRef.current.schedule({
        id: d.id,
        patch: applyLaneTrim({
          layer: l, edge: d.mode === 'trim-start' ? 'start' : 'end', deltaSec,
          clipDuration: clipDurationRef.current,
        }),
      });
    }
  }, []);

  const endDrag = useCallback(() => {
    patchSchedulerRef.current.flush();
    // Remember, for the `click` that fires right after pointerup, whether this
    // gesture was a drag. A drag must not also seek — including a purely
    // vertical one, which leaves start/end untouched but still isn't a click.
    gestureMovedRef.current = Boolean(drag.current && (drag.current.movedX || drag.current.movedY));
    drag.current = null;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', endDrag);
    window.removeEventListener('pointercancel', endDrag);
  }, [onPointerMove]);

  /** Move the playhead to the layer's first visible moment, so the user sees
   *  it. No-op when the playhead already sits inside the layer.
   *
   *  Only ever called from `onClick`, and only when the gesture was not a drag:
   *  seeking on pointerdown would yank the frame away the instant you grabbed a
   *  lane to move or trim it, and the `click` that follows a drag would seek to
   *  the layer's NEW start. */
  const seekToLayer = useCallback((layer: MediaLayer) => {
    if (gestureMovedRef.current) return;
    const target = resolveLayerSeekTarget(layer, intervalsRef.current, currentTimeRef.current);
    if (target !== null) onSeekRef.current?.(target);
  }, []);

  const beginDrag = useCallback((
    e: React.PointerEvent, layer: MediaLayer, mode: DragState['mode'], index: number,
  ) => {
    // Only the primary button drags a lane; right-click opens the timeline
    // context menu instead of moving/trimming the layer.
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    onSelect(layer.id);
    drag.current = {
      id: layer.id, mode, movedX: false, movedY: false,
      startClientX: e.clientX, startClientY: e.clientY, startIndex: index, original: layer,
    };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);
  }, [onSelect, onPointerMove, endDrag]);

  // Detach any in-flight drag listeners on unmount (or when the move handler
  // identity changes) so an unmount mid-drag can't leak window listeners
  // that keep firing into a stale onPatch closure.
  useEffect(() => endDrag, [endDrag]);

  return (
    <>
      {layers.map((layer, idx) => {
        const segments = sequenceMode
          ? [{ videoStart: layer.start, videoEnd: layer.end }]
          : layerWindowToVideoSegments(layer.start, layer.end, intervals);
        // Rows run front-to-back: the last array element (frontmost) is row 0,
        // so dragging a lane up brings its layer toward the camera.
        const top = laneTop + laneRowForIndex(idx, layers.length) * LANE_HEIGHT;
        const Icon = TYPE_ICON[layer.type];
        const active = layer.id === activeLayerId;
        return (
          <div key={layer.id} data-layer-ui data-testid={`layer-lane-${layer.type}`}>
            {segments.map((seg, si) => {
              const first = si === 0;
              const last = si === segments.length - 1;
              return (
                <div
                  key={si}
                  onPointerDown={(e) => beginDrag(e, layer, 'move', idx)}
                  onClick={(e) => { e.stopPropagation(); onSelect(layer.id); seekToLayer(layer); }}
                  className={`absolute h-4 cursor-grab rounded border ${
                    active ? 'bg-primary/60 border-primary' : 'bg-primary/30 border-primary/60'
                  }`}
                  style={{
                    top,
                    left: seg.videoStart * pxPerSecond,
                    width: Math.max(4, (seg.videoEnd - seg.videoStart) * pxPerSecond),
                  }}
                  title={layer.type === 'motion' ? `${t('projectEditor.layers.motion')} · ${layer.name}` : layer.name}
                >
                  {first && (
                    <span className="pointer-events-none absolute left-1.5 top-1/2 flex -translate-y-1/2 items-center gap-1 overflow-hidden whitespace-nowrap text-[10px] leading-none text-primary-foreground">
                      <Icon className="h-2.5 w-2.5 shrink-0" />
                      {layer.type === 'motion' ? `${t('projectEditor.layers.motion')} · ${layer.name}` : layer.name}
                    </span>
                  )}
                  {first && (
                    <div
                      className="absolute left-0 top-0 h-full w-1.5 cursor-ew-resize bg-primary"
                      onPointerDown={(e) => beginDrag(e, layer, 'trim-start', idx)}
                    />
                  )}
                  {last && (
                    <div
                      className="absolute right-0 top-0 h-full w-1.5 cursor-ew-resize bg-primary"
                      onPointerDown={(e) => beginDrag(e, layer, 'trim-end', idx)}
                    />
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </>
  );
};

export default LayerLanes;
